import React, { Suspense } from 'react';
import { useState, useEffect, useRef } from 'react';
import 'assets/scss/charts.scss';
import StatisticGraphComponent from './StatisticGraphComponent';
import DemographicChartComponent from './DemographicChartComponent';
import OpenClientWidgetComponent from './OpenClientWidgetComponent';
import OpenSuppliersWidgetComponent from './OpenSuppliersWidgetComponent';
import TopSuppliersWidgetComponent from './TopSuppliersWidgetComponent';
import TopClientsWidgetComponent from './TopClientsWidgetComponent';
import InvoiceComparisonWidgetComponent from './InvoiceComparisonWidgetComponent';
import ClientPortalRequestWidgetComponent from './ClientPortalRequestWidgetComponent';
import TopProductsWidgetComponent from './TopProductsWidgetComponent';
import { getLocalStorageCacheItem, setLocalStorageCacheItem } from 'helpers/common.helper';
import md5 from 'md5-hash';
import { useQuery } from 'react-query';
import moment from 'moment';
import PermissionAllowed from 'ui-component/permissions/PermissionAllowed';
import TopSalesManagersWidgetComponent from './TopSalesManagersWidgetComponent';
import OpenInvoicesWidgetComponent from './OpenInvoicesWidgetComponent';
import WarriorManagerWidgetComponent from './WarriorManagerWidgetComponent';

import ReactSelect from 'react-select';
import CurrencyExchangeWidgetComponent from './CurrencyExchangeWidgetComponent';
import PerformanceScoreWidget from './PerformanceScoreWidget';
import StatCard from './StatCard';
import usePageTitle from '../../../../hooks/usePageTitle';

import useIsMobile from 'utils/isMobileUtils';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];


const DashboardComponent = ({filters}) => {
	const date = new Date();
	const monthNames = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December'
	];
	const [setPageTitle] = usePageTitle('Dashboard');
	const [currentDate, setDate] = useState(new Date());
	const [currentMonth, setCurrentMonth] = useState(monthNames[date.getMonth()]);
	const [currentYear, setCurrentYear] = useState(date.getFullYear());
	const [activeMonth, setActiveMonth] = useState(`${currentMonth} ${currentYear}`);
	const [month, year] = activeMonth.split(' ');
	const [amount, setAmount] = useState(1.0);
	const [fromCurrency, setFromCurrency] = useState('USD');
	const [toCurrency, setToCurrency] = useState('EUR');
	const [isChartLoaded, setIsChartLoaded] = useState(false);

	const activeDate = new Date(`${month} 1, ${year}`);


	const currentMonthValue = new Date().getMonth() + 1;
	const isMobile = useIsMobile();

	const [selectedYear, setSelectedYear] = useState(currentYear);
	const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
	const [isOverall, setIsOverall] = useState(true);

	const statisticYears = Array.from({ length: 5 }, (_, i) => currentYear - i);
	const statisticMonths = [
		{ value: 0, name: 'Select Month' },
		{ value: 1, name: 'January' }, { value: 2, name: 'February' },
		{ value: 3, name: 'March' }, { value: 4, name: 'April' },
		{ value: 5, name: 'May' }, { value: 6, name: 'June' },
		{ value: 7, name: 'July' }, { value: 8, name: 'August' },
		{ value: 9, name: 'September' }, { value: 10, name: 'October' },
		{ value: 11, name: 'November' }, { value: 12, name: 'December' }
	];
	const [selectedSalesManager, setSelectedSalesManager] = useState('');
	const salesManagerFilter = filters?.find((f) => f.name === "sales_manager_id");

	// Options for react-select
	const monthOptions = statisticMonths.map((m) => ({ value: m.value, label: m.name }));
	const yearOptions = statisticYears.map((y) => ({ value: y, label: y.toString() }));
	const salesManagerOptions = salesManagerFilter ? [{ value: '', label: 'All' }, ...salesManagerFilter.options] : [];
	const formattedDate = isOverall
		? `${currentDate.toLocaleString('en-US', { month: 'short' })}, ${currentDate.getFullYear()}`
		: `${new Date(selectedYear, selectedMonth - 1).toLocaleString('en-US', { month: 'short' })}, ${selectedYear}`;

	const scrollContainer = useRef(null);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (scrollContainer.current) {
				scrollContainer.current.scrollLeft = scrollContainer.current.scrollWidth;
			}
		}, 100);

		return () => clearTimeout(timer);
	}, [months]);

	useEffect(() => {
		const exchangeRate = {
			USD: { USD: 1, EUR: 0.85, GBP: 0.76, JPY: 110.61 },
			EUR: { USD: 1.18, EUR: 1, GBP: 0.89, JPY: 130.14 },
			GBP: { USD: 1.32, EUR: 1.12, GBP: 1, JPY: 146.78 },
			JPY: { USD: 0.009, EUR: 0.0077, GBP: 0.0068, JPY: 1 }
		};

		if (exchangeRate[fromCurrency] && exchangeRate[fromCurrency][toCurrency]) {
		} else {
			throw new Error(`No exchange rate available from ${fromCurrency} to ${toCurrency}`);
		}
	}, [amount, fromCurrency, toCurrency]);

	const [isLoading, setIsLoading] = useState(true);


	const [regionValues, setRegionValues] = useState(null);
	const [regionDatas, setRegionDatas] = useState(null);
	const [openClients, setOpenClients] = useState([]);
	const [openSuppliers, setOpenSuppliers] = useState([]);
	const [topClients, setTopClients] = useState([]);
	const [topSuppliers, setTopSuppliers] = useState([]);
	const [topProducts, setTopProducts] = useState([]);
	const [invoicePaymentSummary, setInvoicePaymentSummary] = useState([]);
	const [clientPortalRequests, setClientPortalRequests] = useState([]);
	const [topSalesManagers, setTopSalesManagers] = useState([]);
	const [topInvoices, setTopInvoices] = useState([]);
	const [warriorManagers, setWarriorManagers] = useState([]);
	const [productsSold, setProductsSold] = useState(0);
	const [creditNotesAmounts, setCreditNotesAmounts] = useState([]);

	const {
		data: reqData,
		refetch,
		isFetching
	} = useQuery(
		['dashboard', selectedMonth, selectedYear, isOverall, selectedSalesManager],
		() => {
			const params = isOverall ? { isOverAll: true } : { year: selectedYear };
			if (!isOverall && selectedMonth) {
				params.month = selectedMonth;
			}

			if (selectedSalesManager) {
				params.sales_person_id = selectedSalesManager;
			}

			return api.getDashboardStatisticsByCountry(params);
		},
		{
			enabled: true,
			onSuccess: (e) => {
				// setIsLoading(false);
				const cacheKey = md5(`dashboard-${selectedYear}-${selectedMonth}`);
				setLocalStorageCacheItem(cacheKey, e, moment().add(3, 'days').format('YYYY-MM-DD'));
			},
			staleTime: 24 * 60 * 60 * 1000,
			refetchOnMount: 'always'
		}
	);

	useEffect(() => {
		setIsChartLoaded(true)
		const formattedWorldMap = reqData?.data?.worldmap?.reduce((acc, item) => {
			acc[item.country] = item.count;
			return acc;
		}, {});
		setRegionValues(formattedWorldMap);
		setRegionDatas(reqData?.data?.worldmap)

		const formattedOpenClients = reqData?.data?.open_clients.map((item) => {
			return {
				...item,
				name: item.name,
				total: parseFloat(item.total).toFixed(2)
			}
		})
			.sort((a, b) => parseFloat(b.total) - parseFloat(a.total));
		setOpenClients(formattedOpenClients);

		const formattedOpenSuppliers = reqData?.data?.open_suppliers.map((item) => {
			return {
				...item,
				name: item.name,
				total: parseFloat(item.total).toFixed(2)
			}
		})
			.sort((a, b) => parseFloat(b.total) - parseFloat(a.total));
		setOpenSuppliers(formattedOpenSuppliers);

		const formattedTopClients = reqData?.data?.top_clients.map((item) => {
			return {
				...item,
				name: item.name,
				total: parseFloat(item.total).toFixed(2)
			}
		})
			.sort((a, b) => parseFloat(b.total) - parseFloat(a.total));
		setTopClients(formattedTopClients);

		const formattedTopSuppliers = reqData?.data?.top_suppliers.map((item) => {
			return {
				...item,
				name: item.name,
				total: parseFloat(item.total).toFixed(2)
			}
		})
			.sort((a, b) => parseFloat(b.total) - parseFloat(a.total));
		setTopSuppliers(formattedTopSuppliers);

		setTopProducts(reqData?.data?.top_products)

		setInvoicePaymentSummary(reqData?.data?.invoice_payment_summary)

		// Only include requests with status 'Pending'
		setClientPortalRequests((reqData?.data?.client_portal_requests || []).filter(r => r.status === 'Pending'))

		setTopSalesManagers(reqData?.data?.top_sales_managers)
	
		setTopInvoices(reqData?.data?.top_invoices)

		setWarriorManagers(reqData?.data.getWarriorOfTheMonth)

		setProductsSold(reqData?.data.total_products_sold)

		setCreditNotesAmounts(reqData?.data.get_credit_notes_amount.credit_notes)

	}, [reqData?.data])

	useEffect(() => {
		if (!getLocalStorageCacheItem(md5('dashboard'))) {
			setIsLoading(true)
		}
	}, [])


	if (!isMobile) {
		return (
			<>
				<div className="global-time row m-0 mt-4" style={{ display: 'flex', justifyContent: 'space-between' }}>
					<a>
						{new Date().toLocaleString('en-US', {
							month: 'short',
							day: 'numeric',
							year: 'numeric',
							hour: 'numeric',
							minute: 'numeric',
							hour12: true
						})}
					</a>
					<div style={{ marginRight: '20px' }}>
						<a className="multiple-times">
							New York{' '}
							{currentDate.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false })}
						</a>
						<b className="multiple-times">|</b>
						<a className="multiple-times">
							London {currentDate.toLocaleString('en-US', { timeZone: 'Europe/London', hour: 'numeric', minute: 'numeric', hour12: false })}
						</a>
						<b className="multiple-times">|</b>
						<a className="multiple-times">
							Amsterdam{' '}
							{currentDate.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam', hour: 'numeric', minute: 'numeric', hour12: false })}
						</a>
						<b className="multiple-times">|</b>
						<a className="multiple-times">
							Hong Kong{' '}
							{currentDate.toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong', hour: 'numeric', minute: 'numeric', hour12: false })}
						</a>
					</div>
				</div>
				<div className="row" style={{ padding: '10px'}}>

					{/* Filter Card */}
					<div
						className="col-12"
						style={{
							position: 'sticky',
							top: 0,
							zIndex: 10,
							padding: '8px 10px'
						}}
					>
						<div className="card pt-3 px-3 shadow-sm">
							<div className={`${isMobile ? '' : 'gap-3' } row g-3 align-items-end justify-content-lg-end`}>
								<div className={`${isMobile ? 'col-lg-2 col-md-3 col-sm-6 col-6' : '' } form-group`} style={{ width: '150px' }}>
									<ReactSelect
										id="month-filter"
										className="rs-container w-full"
										classNamePrefix="rs"
										options={monthOptions}
										value={monthOptions.find((o) => o.value === selectedMonth) || null}
										onChange={(option) => setSelectedMonth(option.value)}
										isDisabled={isOverall || isFetching}
										menuPosition="fixed"
										placeholder="Select Month..."
									/>
								</div>

								<div className={`${isMobile ? 'col-lg-2 col-md-3 col-sm-6 col-6' : '' } form-group`}  style={{ width: '150px' }}>
									<ReactSelect
										id="year-filter"
										className="rs-container"
										classNamePrefix="rs"
										options={yearOptions}
										value={yearOptions.find((o) => o.value === selectedYear)}
										onChange={(option) => setSelectedYear(option.value)}
										isDisabled={isOverall || isFetching}
										menuPosition="fixed"
										placeholder="Select Year..."
									/>
								</div>

								{salesManagerFilter && (
									<div className={`${isMobile ? 'col-lg-2 col-md-3 col-sm-6 col-6' : '' } form-group`} style={{ width: '150px' }}>
										<ReactSelect
											className="rs-container"
											classNamePrefix="rs"
											options={salesManagerOptions}
											value={
												salesManagerOptions.find((o) => o.value === selectedSalesManager)
											}
											onChange={(option) => setSelectedSalesManager(option.value)}
											isDisabled={isFetching}
											menuPosition="fixed"
											placeholder="Select Manager..."
										/>
									</div>
								)}

								<div className="col-lg-auto col-md-3 col-sm-6 col-6 d-flex align-items-end pb-1 form-group">
									<div className="form-check pb-2">
										<input
											type="checkbox"
											id="overall-checkbox"
											className="form-check-input"
											checked={isOverall}
											onChange={(e) => {
												setIsOverall(e.target.checked);
												setTimeout(() => refetch(), 0);
											}}
											disabled={isFetching}
										/>
										<label className="form-check-label" htmlFor="overall-checkbox">
											Overall
										</label>
									</div>
								</div>
							</div>
						</div>
					</div>
					{/* End Filter Card */}

					<PermissionAllowed permissions={['List Estimation (Page)']}>
						<div className="col-12" style={{ padding: '10px' }}>
							<StatisticGraphComponent selectedSalesManager={selectedSalesManager} />
						</div>
					</PermissionAllowed>

					<div className='col-12 dashboard-widget-container'>
						<div className="row">

							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="form-group col-12 col-sm-12 col-md-12 col-lg-6 col-xxl-6">
									<WarriorManagerWidgetComponent details={warriorManagers} isLoading={isFetching} currentDate={formattedDate} />
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="col-12 col-sm-6 col-md-6 col-lg-3 col-xxl-12">
									<StatCard
										isLoading={isFetching}
										title="Product sold"
										// details={productAmounts ?? {}}
										details={productsSold ?? 0}
										isPositive={true}
										isPrice={false}
									/>

									<StatCard
										isLoading={isFetching}
										title="Credit notes"
										details={creditNotesAmounts ?? {}}
										isPositive={false}
										isPrice={true}
									/>
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="form-group col-12 col-sm-6 col-md-6 col-lg-3 col-xxl-12">
									<CurrencyExchangeWidgetComponent />
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Estimation (Page)']}>
								<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
									<PerformanceScoreWidget details={topSalesManagers} isLoading={isFetching} />
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
									<TopSalesManagersWidgetComponent details={topSalesManagers} isLoading={isFetching} />
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Estimation (Page)']}>
								<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
									<InvoiceComparisonWidgetComponent
										details={invoicePaymentSummary}
										isLoading={isFetching}
										loadChart={isChartLoaded}
									/>
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Estimation (Page)']}>
								<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
									<OpenInvoicesWidgetComponent
										details={topInvoices}
										isLoading={isFetching}
									/>
								</div>
							</PermissionAllowed>

							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
									<OpenClientWidgetComponent
										details={openClients}
										isLoading={isFetching}
									/>
								</div>
								<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
									<OpenSuppliersWidgetComponent
										details={openSuppliers}
										isLoading={isFetching}
									/>
								</div>
								<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
									<TopClientsWidgetComponent
										details={topClients}
										isLoading={isFetching}
									/>
								</div>
								<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
									<TopSuppliersWidgetComponent
										details={topSuppliers}
										isLoading={isFetching}
									/>
									<div className="show-dashboard-widget-on-tablet-size">
										<div style={{ marginTop: '20px' }}>
										</div>
									</div>
								</div>
							</PermissionAllowed>
							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xxl-4" style={{ marginBottom: '20px' }}>
									<TopProductsWidgetComponent
										details={topProducts}
										isLoading={isFetching}
									/>
								</div>
							</PermissionAllowed>
							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xxl-4" style={{ marginBottom: '20px' }}>
									<DemographicChartComponent
										mapCount={regionValues}
										mapList={regionDatas}
										isLoading={isFetching}
									/>
								</div>
							</PermissionAllowed>
							<PermissionAllowed permissions={['List Contact (Page)']}>
								<div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xxl-4" style={{ marginBottom: '20px' }}>
									<ClientPortalRequestWidgetComponent
										details={clientPortalRequests}
										isLoading={isFetching}
									/>
								</div>
							</PermissionAllowed>
						</div>
					</div>

					<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6 col-xxl-4" style={{ padding: '10px' }}>
					</div>
					<div className="col-12 col-sm-6 col-md-6 col-lg-4" style={{ padding: '10px' }}>
					</div>
					<div className="col-12 col-sm-6 col-md-6 col-lg-2" style={{ padding: '10px'}}>
					</div>
					<div className="col-12 col-sm-6 col-md-6 col-lg-3" style={{ padding: '10px'}}>
					</div>
					<div className="col-12 col-sm-6 col-md-6 col-lg-3" style={{ padding: '10px' }}>
					</div>
				</div>
			</>
		)
	} else {
		return (
			<div className="row justify-content-center m-0">
				<div className="medium-sized-container" style={{ marginBottom: '50px' }}>
					
					<div className="col-xl-12 col-lg-12 p-0">
						<div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
							<div className="header-bubble" />
							<div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
								<div className="global-time row m-0 mb-4" style={{ display: 'flex', justifyContent: 'space-between' }}>
									<a>
										{new Date().toLocaleString('en-US', {
											month: 'short',
											day: 'numeric',
											year: 'numeric',
											hour: 'numeric',
											minute: 'numeric',
											hour12: true
										})}
									</a>
									<div style={{ marginRight: '20px' }}>
										<a className="multiple-times">
											New York{' '}
											{currentDate.toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false })}
										</a>
										<b className="multiple-times">|</b>
										<a className="multiple-times">
											London {currentDate.toLocaleString('en-US', { timeZone: 'Europe/London', hour: 'numeric', minute: 'numeric', hour12: false })}
										</a>
										<b className="multiple-times">|</b>
										<a className="multiple-times">
											Amsterdam{' '}
											{currentDate.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam', hour: 'numeric', minute: 'numeric', hour12: false })}
										</a>
										<b className="multiple-times">|</b>
										<a className="multiple-times">
											Hong Kong{' '}
											{currentDate.toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong', hour: 'numeric', minute: 'numeric', hour12: false })}
										</a>
									</div>
								</div>
								<div
									style={{
										paddingLeft: 20,
										paddingRight: 20,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										gap: 10,
										paddingBottom: 4
									}}
								>
								</div>
							</div>
						</div>
						
					</div>
					<div style={{ marginTop: '-15px', height: 'calc(100vh - 152px)', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#0966ff transparent' }}>

						<div className="row" style={{ margin: 0 }}>

							<div
								className="col-12"
								style={{
									top: 0,
									zIndex: 5,
									padding: '8px 10px'
								}}
							>
								<div className="card pt-3 px-3 shadow-sm">
									<div className={`${isMobile ? '' : 'gap-3' } row g-3 align-items-end justify-content-lg-end`}>
										<div className={`${isMobile ? 'col-lg-2 col-md-3 col-sm-6 col-6' : '' } form-group`} style={{ width: '150px' }}>
											<ReactSelect
												id="month-filter"
												className="rs-container w-full"
												classNamePrefix="rs"
												options={monthOptions}
												value={monthOptions.find((o) => o.value === selectedMonth) || null}
												onChange={(option) => setSelectedMonth(option.value)}
												isDisabled={isOverall || isFetching}
												menuPosition="fixed"
												placeholder="Select Month..."
											/>
										</div>

										<div className={`${isMobile ? 'col-lg-2 col-md-3 col-sm-6 col-6' : '' } form-group`}  style={{ width: '150px' }}>
											<ReactSelect
												id="year-filter"
												className="rs-container"
												classNamePrefix="rs"
												options={yearOptions}
												value={yearOptions.find((o) => o.value === selectedYear)}
												onChange={(option) => setSelectedYear(option.value)}
												isDisabled={isOverall || isFetching}
												menuPosition="fixed"
												placeholder="Select Year..."
											/>
										</div>

										{salesManagerFilter && (
											<div className={`${isMobile ? 'col-lg-2 col-md-3 col-sm-6 col-6' : '' } form-group`} style={{ width: '150px' }}>
												<ReactSelect
													className="rs-container"
													classNamePrefix="rs"
													options={salesManagerOptions}
													value={
														salesManagerOptions.find((o) => o.value === selectedSalesManager)
													}
													onChange={(option) => setSelectedSalesManager(option.value)}
													isDisabled={isFetching}
													menuPosition="fixed"
													placeholder="Select Manager..."
												/>
											</div>
										)}

										<div className="col-lg-auto col-md-3 col-sm-6 col-6 d-flex align-items-end pb-1 form-group">
											<div className="form-check pb-2">
												<input
													type="checkbox"
													id="overall-checkbox"
													className="form-check-input"
													checked={isOverall}
													onChange={(e) => {
														setIsOverall(e.target.checked);
														setTimeout(() => refetch(), 0);
													}}
													disabled={isFetching}
												/>
												<label className="form-check-label" htmlFor="overall-checkbox">
													Overall
												</label>
											</div>
										</div>
									</div>
								</div>
							</div>

					

							<PermissionAllowed permissions={['List Estimation (Page)']}>
								<div className="col-12" style={{ padding: '10px 20px' }}>
									<StatisticGraphComponent selectedSalesManager={selectedSalesManager} />
								</div>
							</PermissionAllowed>



							<div className='col-12 dashboard-widget-container' style={{ padding: '10px 20px' }}>
								<div className="row">

									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="form-group col-12 col-sm-12 col-md-12 col-lg-6 col-xxl-6">
											<WarriorManagerWidgetComponent details={warriorManagers} isLoading={isFetching} currentDate={formattedDate} />
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="col-12 col-sm-6 col-md-6 col-lg-3 col-xxl-12">
											<StatCard
												isLoading={isFetching}
												title="Product sold"
												// details={productAmounts ?? {}}
												details={productsSold ?? 0}
												isPositive={true}
												isPrice={false}
											/>

											<StatCard
												isLoading={isFetching}
												title="Credit notes"
												details={creditNotesAmounts ?? {}}
												isPositive={false}
												isPrice={true}
											/>
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="form-group col-12 col-sm-6 col-md-6 col-lg-3 col-xxl-12">
											<CurrencyExchangeWidgetComponent />
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Estimation (Page)']}>
										<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
											<PerformanceScoreWidget details={topSalesManagers} isLoading={isFetching} />
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
											<TopSalesManagersWidgetComponent details={topSalesManagers} isLoading={isFetching} />
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Estimation (Page)']}>
										<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
											<InvoiceComparisonWidgetComponent
												details={invoicePaymentSummary}
												isLoading={isFetching}
												loadChart={isChartLoaded}
											/>
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Estimation (Page)']}>
										<div className="form-group col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3">
											<OpenInvoicesWidgetComponent
												details={topInvoices}
												isLoading={isFetching}
											/>
										</div>
									</PermissionAllowed>

									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
											<OpenClientWidgetComponent
												details={openClients}
												isLoading={isFetching}
											/>
										</div>
										<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
											<OpenSuppliersWidgetComponent
												details={openSuppliers}
												isLoading={isFetching}
											/>
										</div>
										<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
											<TopClientsWidgetComponent
												details={topClients}
												isLoading={isFetching}
											/>
										</div>
										<div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xxl-3" style={{ marginBottom: '20px' }}>
											<TopSuppliersWidgetComponent
												details={topSuppliers}
												isLoading={isFetching}
											/>
											<div className="show-dashboard-widget-on-tablet-size">
												<div style={{ marginTop: '20px' }}>
												</div>
											</div>
										</div>
									</PermissionAllowed>
									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xxl-4" style={{ marginBottom: '20px' }}>
											<TopProductsWidgetComponent
												details={topProducts}
												isLoading={isFetching}
												// isOverall={isOverall}
												// selectedMonth={selectedMonth}
												// selectedYear={selectedYear}
												// selectedSalesManager={selectedSalesManager}
											/>
										</div>
									</PermissionAllowed>
									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xxl-4" style={{ marginBottom: '20px' }}>
											<DemographicChartComponent
												mapCount={regionValues}
												mapList={regionDatas}
												isLoading={isFetching}
											/>
										</div>
									</PermissionAllowed>
									<PermissionAllowed permissions={['List Contact (Page)']}>
										<div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xxl-4" style={{ marginBottom: '20px' }}>
											<ClientPortalRequestWidgetComponent
												details={clientPortalRequests}
												isLoading={isFetching}
											/>
										</div>
									</PermissionAllowed>
								</div>
							</div>


							

					
						</div>

					</div>


					

				</div>
			</div>
		)
	}

};

export default DashboardComponent;